import { EMAIL, EmbeddedMessage, SMS, WhatsApp } from 'constants/messageMediums';
import { formatSegmentName } from 'modules/messagingInsights/components/Audience/utils';
import { TruncatedHeaderCell } from 'modules/messagingInsights/components/TruncatedHeaderCell';
import {
  Cell,
  CenterText,
  RightAlignText,
  TabularNumberFont,
} from 'modules/productAnalytics/Styled';
import {
  formatNumberGivenValueType,
  isSMSClickMetric,
  metricSentenceCase,
} from 'modules/productAnalytics/utils';
import moment from 'moment';
import { isEmpty } from '@iterable/utils';
// import { MetricOption } from 'modules/messagingInsights/types';
import { displayDate } from 'utils/date';
import { MetricDefinitionWrapper } from './components/MetricDefinitionWrapper';
import {
  BASE_TIME_SERIES_COLUMN,
  CIRCLE,
  CONVERSION_METRICS_PER_MESSAGE_MEDIUM,
  ConversionMetricOptions,
  DEVICE_DATA,
  DIAMOND,
  DOWN_TRIANGLE,
  DRAFT,
  EmailListName,
  GREEN_SHADES,
  InAppToBreakdown,
  PRE_ACTIVATION_STATES,
  PRE_READY_STATES,
  QueryValueOptionsByParamCA,
  READY,
  READY_STATES,
  RUNNING,
  SCHEDULED,
  SQUARE,
  UP_TRIANGLE,
  YELLOW_SHADES,
} from './constants';
import { CONVERSION_COLORS, CONVERSION_SHAPES } from './types';

export const getCampaignDetailsLink = ({ campaignMetadata, campaignId }) => {
  // Campaign reactification path
  if (PRE_READY_STATES.includes(campaignMetadata?.campaignState)) {
    return `/campaigns/${campaignId}?view=Edit`;
  }
  if (READY_STATES.includes(campaignMetadata?.campaignState)) {
    return `/campaigns/${campaignId}?view=Summary`;
  }
  // legacy path
  if (PRE_ACTIVATION_STATES.includes(campaignMetadata?.campaignState)) {
    return `/campaigns/${campaignId}?view=Edit`;
  }

  return `/campaigns/${campaignId}?view=Summary`;
};

export const getOrderedSelection = (orderedList = [], selection) => {
  const selectionSet = new Set(selection);
  return orderedList.filter((el) => selectionSet.has(el));
};

export const parseMetricsForBreakdown = (metricList, results = {}) => {
  if (Object.keys(results).length === 0) {
    return [];
  }
  return metricList.map((metric) => [
    {
      name: metricSentenceCase(metric),
      value: results[metric]?.value,
      key: metric,
    },
  ]);
};

export const getSegmentCols = (segmentName = '', isDate) => {
  const segmentCols = [
    {
      name: 'Segment',
      value: 'Segment',
      align: 'flex-start',
      RowCell: ({ row }) => (
        <Cell>
          <CenterText>
            <TruncatedHeaderCell
              name={isDate ? displayDate(moment.unix(row.Segment / 1000)) : row.Segment}
              textOnly
            />
          </CenterText>
        </Cell>
      ),
    },
  ];
  if (segmentName === 'emailListIds' || segmentName === 'userListIds') {
    segmentCols.push(
      segmentName === 'emailListIds' ? EmailListName : { ...EmailListName, name: 'List Name' },
    );
  }
  return segmentCols;
};

export const getMetricColumns = (metrics, valueTypes, inSegmentAnalysis = false) => {
  const metricCols = [];
  metrics.forEach((val) => {
    metricCols.push({
      name: (
        <MetricDefinitionWrapper metricName={val} inSegmentAnalysis={inSegmentAnalysis}>
          {metricSentenceCase(val)}
        </MetricDefinitionWrapper>
      ),
      value: val,
      align: 'flex-end',
      RowCell: ({ row }) => (
        <Cell>
          <RightAlignText>
            <TabularNumberFont>
              {formatNumberGivenValueType(valueTypes[val], row[val])}
            </TabularNumberFont>
          </RightAlignText>
        </Cell>
      ),
    });
  });
  return metricCols;
};

export const getMetricArray = (segmentData = {}) => {
  const metricSet = new Set();
  Object.values(segmentData).forEach((seg) => {
    Object.keys(seg).forEach((segMetric) => {
      if (segMetric !== 'Segment' && segMetric !== 'EmailListName') {
        metricSet.add(segMetric);
      }
    });
  });

  return Array.from(metricSet);
};

export const TEST_3_DECIMALS = new RegExp(`\\.\\d{3,}$`); // Regex to test if there are 3 or more digits after dot

export const formatPercentage = (percentage /*: number */) => {
  if (TEST_3_DECIMALS.test(`${percentage}`)) {
    return `${percentage.toFixed(2)}%`;
  } else {
    return `${percentage}%`;
  }
};

export const parseInAppMetrics = (metricName, metricLabel, results, dividendValue) => {
  const colorObj = {
    'Total In-apps delivered': GREEN_SHADES,
    'Total inbox impressions': YELLOW_SHADES,
  };

  const totalVal = results[metricName]?.value;
  const destinations = results[InAppToBreakdown[metricName]]?.value?.destinations || [];
  const progressData = [];
  const otherDestinations = {
    platform: undefined,
    appName: 'Other',
    percentage: 0,
    value: 0,
  };
  let colorIdxDiff = 0;
  destinations.forEach((destination, idx) => {
    if (destination?.appName) {
      progressData.push({
        platform: destination?.platform,
        appName: destination?.appName,
        percentage: (destination?.value / dividendValue) * 100,
        color: colorObj[metricLabel][(idx - colorIdxDiff) % colorObj[metricLabel].length],
        value: destination?.value,
      });
    } else {
      otherDestinations.value += destination?.value || 0;
      otherDestinations.percentage += (destination?.value / dividendValue) * 100 || 0;
      colorIdxDiff += 1;
    }
  });
  if (otherDestinations.value > 0) {
    progressData.push({
      ...otherDestinations,
      color:
        colorObj[metricLabel][(destinations.length - colorIdxDiff) % colorObj[metricLabel].length],
    });
  }
  return {
    name: metricLabel,
    value: totalVal,
    progressData,
  };
};

export const parseMetricsForDeliveriesGraph = (metricsList, results = {}, buckets = {}, inAppFF) =>
  metricsList.map((metricData) => {
    const metricName = metricData.metric;
    const metricLabel = metricData.label;
    const dividendValue = results[metricData.dividendMetric]?.value;
    if (
      inAppFF &&
      (metricName === 'Total In-apps Delivered' || metricName === 'Total Inbox Impressions')
    ) {
      return parseInAppMetrics(metricName, metricLabel, results, dividendValue);
    }
    const value = results[metricData.metric]?.value;
    const displayValue = metricData.isRate ? formatPercentage(value * 100) : value;
    if (metricData?.buckets?.length && !isEmpty(buckets)) {
      return {
        name: metricData.label,
        value: displayValue,
        progressData: metricData.buckets.map((bucket) => ({
          name: bucket.label,
          percentage: bucket.isRate
            ? buckets[bucket.metric.percentage] * 100
            : buckets[bucket.metric.percentage],
          color: bucket.color,
        })),
      };
    } else {
      const percentage = metricData.isRate
        ? value * 100
        : (value / results[metricData.dividendMetric]?.value) * 100;
      return {
        name: metricData.label,
        value: displayValue,
        progressData: [{ color: metricData.color, percentage }],
      };
    }
  });

export const parseMetricsForRetentionGraph = (metricData, results = {}) => {
  if (metricData) {
    const data =
      results[metricData.graphMetric]?.histogram?.buckets?.map(({ time, value }) => [
        time,
        value,
      ]) || [];
    return { label: metricData.graphMetricLabel, data };
  }
  return null;
};

export const parseMetricsForTimeSeriesDropdown = (metricsList, results = {}) =>
  metricsList
    .filter((metricData) => !!results[metricData.label]?.histogram?.buckets)
    .map((metric) => ({ ...metric, label: metricSentenceCase(metric.label) }));

export const parseMetricsForTimeSeriesGraph = (metricsList, results = {}) => {
  const graphData = [];
  metricsList.forEach((metricData) => {
    const buckets = results[metricData]?.histogram?.buckets;
    if (buckets) {
      const data = buckets.map(({ time, value }) => [time, value]);
      graphData.push({ name: metricSentenceCase(metricData), data });
    }
  });
  return graphData;
};

export const parseMetricsForTimeSeriesTable = (metricsList, results = {}, valueTypes) => {
  const rowsDateTimeMap = {};
  const columns = [BASE_TIME_SERIES_COLUMN];
  metricsList.forEach((metric) => {
    const buckets = results[metric]?.histogram?.buckets;
    if (buckets) {
      columns.push(...getMetricColumns([metric], valueTypes));
      buckets.forEach(({ time, value }) => {
        if (!rowsDateTimeMap[time]) {
          rowsDateTimeMap[time] = {};
        }
        rowsDateTimeMap[time][metric] = value;
      });
    }
  });
  const rows = Object.keys(rowsDateTimeMap).map((dateTime) => ({
    dateTime: parseInt(dateTime, 10),
    ...rowsDateTimeMap[dateTime],
  }));
  return { rows, columns };
};

export const parseMetricsForDevicePieChart = (results = {}) => {
  const deviceDataMap = results[DEVICE_DATA]?.value;
  if (deviceDataMap) {
    return Object.keys(deviceDataMap).map((deviceName) => ({
      name: deviceName,
      y: deviceDataMap[deviceName],
    }));
  }
  return null;
};

export const getHighLevelMetrics = (results) => {
  const uniqueOpensMetric = 'Unique Opens';
  const uniqueOpens = results?.highLevel[uniqueOpensMetric];
  const totalDelivered = results?.highLevel['Total Delivered'];

  return uniqueOpens > 0 || totalDelivered > 0
    ? [
        {
          name: metricSentenceCase(uniqueOpensMetric),
          y: uniqueOpens,
        },
        {
          name: 'Not yet opened',
          y: totalDelivered - uniqueOpens,
        },
      ]
    : [];
};

export const generateSideNavLinks = (
  search,
  campaignMedium,
  showSnippets = false,
  isWhatsAppGAEnabled = false,
) => {
  const isWhatsApp = campaignMedium === WhatsApp;
  const isWhatsAppGA = isWhatsApp && isWhatsAppGAEnabled;

  const monitorSection = {
    header: 'Monitor',
    links: [
      {
        name: 'Overview',
        pathname: '/analytics/campaignPerformance',
        search,
        exact: true,
      },
      {
        name: 'Delivery',
        pathname: '/analytics/campaignPerformance/deliveries',
        search,
      },
    ],
  };
  if (!isWhatsApp || isWhatsAppGA) {
    monitorSection.links = [
      ...monitorSection.links,
      {
        name: 'Engagement',
        pathname: '/analytics/campaignPerformance/engagement',
        search,
      },
    ];
  }
  if (campaignMedium !== SMS && campaignMedium !== EmbeddedMessage) {
    monitorSection.links = [
      ...monitorSection.links,
      {
        name: 'Audience retention',
        pathname: '/analytics/campaignPerformance/retention',
        search,
      },
    ];
  }
  if (isWhatsApp && isWhatsAppGA) {
    monitorSection.links = [
      ...monitorSection.links,
      {
        name: 'Replies',
        pathname: '/analytics/campaignPerformance/replies',
        search,
      },
    ];
  }
  const analyzeSection = {
    header: 'Analyze',
    links: [
      {
        name: 'Segment analysis',
        pathname: '/analytics/campaignPerformance/segments',
        search,
      },
      {
        name: 'Time series',
        pathname: '/analytics/campaignPerformance/timeSeries',
        search,
      },
    ],
  };
  if (campaignMedium === EMAIL) {
    analyzeSection.links = [
      ...analyzeSection.links,
      ...(showSnippets
        ? [
            {
              name: 'Snippets',
              pathname: '/analytics/campaignPerformance/snippets',
              search,
            },
          ]
        : []),
      {
        name: 'Heatmap',
        pathname: '/analytics/campaignPerformance/heatmap',
        search,
      },
      {
        name: 'Locations',
        pathname: '/analytics/campaignPerformance/locations',
        search,
      },
    ];
  }
  return [monitorSection, analyzeSection];
};

export const filterLabels = (updatedLabels, campaignLabels, projectId) => {
  const labelSet = new Set(updatedLabels);
  return campaignLabels.reduce((acc, curr) => {
    if (labelSet.has(curr.name)) {
      return [...acc, { name: curr.name, id: curr.id, projectId }];
    }
    return acc;
  }, []);
};

export const generateNoDataCopy = (state) => {
  switch (state) {
    case RUNNING: {
      return {
        title: 'No data to display yet',
        subtitle:
          "We'll display metrics as recipients engage with your campaign. Check back shortly!",
      };
    }
    case SCHEDULED:
    case DRAFT:
    case READY: {
      return {
        title: "This campaign isn't running yet",
        subtitle: `It's still in ${state} state. Check back after the campaign starts running.`,
      };
    }
    default: {
      return {
        title: 'No data to display yet',
        subtitle:
          "We'll display metrics as recipients engage with your campaign. Check back shortly!",
      };
    }
  }
};

export const maxBy = (arr, func) => {
  const max = Math.max(...arr.map(func));
  return arr.find((item) => func(item) === max);
};
export const extractContentFromJqueryElem = (jqueryElem) => {
  let text;
  let img;
  const url = jqueryElem?.attributes?.href?.value;

  if (jqueryElem.firstElementChild && jqueryElem.firstElementChild.tagName === 'IMG') {
    img = jqueryElem.firstElementChild.src;
  } else if (jqueryElem?.attributes?.href?.content) {
    text = jqueryElem?.attributes?.href?.content;
  } else if (jqueryElem.text) {
    text = jqueryElem.text;
  } else if (jqueryElem.textContent) {
    text = jqueryElem.textContent;
  } else {
    // Didn't match anything
    text = '-';
  }

  // get native DOM element
  const rect = jqueryElem.getBoundingClientRect();

  const x = Math.round(rect.left + (rect.right - rect.left) / 2);
  const y = Math.round(rect.top + (rect.bottom - rect.top) / 2);

  return {
    url,
    img,
    text,
    x,
    y,
  };
};

export const createHeatmap = (data, heatmap, maxCount, useTotalCount) => {
  // Configure the data properly for heatmap.js
  // with the correct xy value pairs
  const heatmapData = data.map((link) => ({
    x: link.x,
    y: link.y,
    value: useTotalCount ? link.totalCount : link.uniqueCount,
  }));

  const newData = {
    max: maxCount,
    min: 0,
    data: heatmapData,
  };
  // Write the new heatmap data
  heatmap.setData(newData);
};

/**
 * This method currently processes the Conversion object of the StatsForTimerangeResponse
 * To align more with the other top level objects in the StatsForTimerangeResponse.
 *
 * It also makes sure that the Conversion object has all the conversion events
 * Which has been selected for a given campaign. And, deletes the empty Conversion object
 * If, no conversion event is selected for a campaign.
 *
 * Transforms the following conversion object
 *
 "Conversions": {
    "value": {
      "event1Name": 131,
      "event2Name": 412,
    },
    "histogram": {
      "buckets": []
    },
    "termsHistogram": [
      [timestamp, {
        "event1Name": 13,
        "event2Name": 76,
      }],
      ...
    ]
  }
  *
  * To the following format
  *
  "Conversions": {
    "value": [{
      eventName,
      eventValue,
      color,
      shape,
      maxEventValueOverTime,
      histogram
    }, ..]
    "histogram": {
      "buckets": []
    },
  }
 */
export const processStatsForTimerangeResponse = (messageMedium, campaignConversions, response) => {
  if (campaignConversions.length) {
    const {
      results: { Conversions = {} } = {},
    } = response;
    const { termsHistogram = [], value: finalValue = {} } = Conversions;

    // The selected campaign conversion events are sorted based on their final values
    // Once, sorted we add a color and shape to be associated with a given conversion event
    const conversionEvents = campaignConversions
      .sort((a, b) => (finalValue[b] ?? 0) - (finalValue[a] ?? 0))
      .map((eventName, conversionIndex) => ({
        eventName,
        eventValue: finalValue[eventName] ?? 0,
        color: CONVERSION_COLORS[conversionIndex % CONVERSION_COLORS.length],
        shape: CONVERSION_SHAPES[conversionIndex % CONVERSION_SHAPES.length],
      }));

    // We do the conversion object transformation in this reduce.
    const rehydratedConversionsData = termsHistogram.reduce((acc, term) => {
      const [atTime, conversionDataAtTime] = term;

      return conversionEvents.map((conversion, conversionIndex) => ({
        ...conversion,
        maxEventValueOverTime: Math.max(
          acc?.[conversionIndex]?.maxEventValueOverTime ?? 0,
          conversionDataAtTime?.[conversion.eventName] ?? 0,
        ),
        histogram: {
          buckets: [
            ...(acc?.[conversionIndex]?.histogram?.buckets ?? []),
            {
              time: atTime,
              value: conversionDataAtTime?.[conversion.eventName] ?? 0,
            },
          ],
        },
      }));
    }, []);

    // We update the Conversion object to match with the rest of the
    // top level objects in the StatsForTimerangeResponse.
    // We also add a 'Total Conversions' object which is the sum value of all the selected conversions of a campaign.
    return {
      ...response,
      results: {
        ...response.results,
        Conversions: {
          histogram: Conversions.histogram,
          value: rehydratedConversionsData,
        },
        'Total Conversions': {
          value: rehydratedConversionsData.reduce((sum, { eventValue }) => sum + eventValue, 0),
        },
      },
    };
  }

  // Delete the Conversions obj from the processed response.
  // Since, no conversion event is selected in the campaign level.
  delete response?.results?.Conversions;
  // We also want to delete the other conversion metrics. So, if a user selects them
  // in the pinned metric section of the analytics page we would display '--' instead of 0.
  // This will differentiate whether a campaign has conversion selected or not.
  ConversionMetricOptions.forEach(({ label }) => delete response?.results?.[label]);
  // Similarly, any metrics related to conversion rates should also be treated the same.
  CONVERSION_METRICS_PER_MESSAGE_MEDIUM[messageMedium].forEach(
    (metric) => delete response?.results?.[metric],
  );
  return response;
};

export const getMultiConversionSymbol = (symbol) => {
  switch (symbol) {
    case DIAMOND:
      return '♦';

    case SQUARE:
      return '■';

    case UP_TRIANGLE:
      return '▲';

    case DOWN_TRIANGLE:
      return '▼';

    case CIRCLE:
    default:
      return '●';
  }
};

export const getTopShapePosition = (shape) => (shape === CIRCLE ? 2 : 0);

/**
 * Returns all valid values for each url parameter.
 */
export const getCampaignAnalyticsParamValueOptions = (
  SMSLinkShorteningFFOn /*: boolean */,
  isCustomConversion /*: boolean */,
  statsForTimeRange,
  AnalyticsMetricsOptions /*: MetricOption[] */,
) => {
  const options = AnalyticsMetricsOptions || [];

  const filterAndMap = (opts /*: MetricOption[] */) =>
    opts
      .filter((el) => SMSLinkShorteningFFOn || !isSMSClickMetric(el.label))
      .map((opt) => opt.value);

  return {
    ...QueryValueOptionsByParamCA,
    overviewMetrics: filterAndMap(options),
    timeSeriesMetrics: filterAndMap(
      parseMetricsForTimeSeriesDropdown(options, statsForTimeRange?.results),
    ),
    segmentMetrics: filterAndMap(
      options.filter(
        (el) =>
          isCustomConversion ||
          (!isCustomConversion && !el.label.toLowerCase().includes('conversion')),
      ),
    ),
  };
};

export const getSegmentVals = (segmentResp, segmentType) => {
  const segmentArray = [];
  const metricsWithData = [];
  if (segmentResp?.mediumMetrics) {
    Object.keys(segmentResp?.mediumMetrics).forEach((key) => {
      const parsedKey = formatSegmentName(key, segmentType);
      const segmentData = { Segment: parsedKey };
      Object.keys(segmentResp?.mediumMetrics[key]).forEach((metric) => {
        segmentData[metric] = segmentResp?.mediumMetrics[key][metric].value;
        metricsWithData.push(metric);
      });
      segmentArray.push(segmentData);
    });
  }
  return segmentArray;
};
